/**
 * Whether a navigation would replace the workbench document (见 06 / 20).
 *
 * The workbench window is frameless with no address bar and no back button, so a
 * top-level navigation that leaves the app strands the user until they restart
 * it. Both runtimes therefore guard navigation, and both need the same question
 * answered: *is the destination still this document?*
 *
 * Comparing scheme + host + path (rather than the whole URL) is what makes the
 * guard usable, because two legitimate cases change the URL without changing the
 * document:
 *
 * - `location.reload()` fires a navigate event whose destination is **not** the
 *   same document, but whose address is identical — measured, not assumed
 *   (`.test_tools/nav-api-spike`);
 * - the workbench reads `?surface=<viewId>` / `?shell=browser` off its own
 *   address (见 08 / 22), so a detached window differs from the main one by query
 *   alone.
 *
 * Query and hash are therefore deliberately ignored: they address a location
 * *inside* the document, which is exactly what a same-document navigation is.
 */

/**
 * Whether `targetUrl` addresses the same document as `baseUrl` — same scheme,
 * host and path, ignoring query and hash.
 *
 * **Not `URL.origin`**, which would be a hole here rather than a shortcut: only
 * *special* schemes (`http(s)`, `ws(s)`, `ftp`, `file`) get a tuple origin, and
 * the desktop workbench is served from a custom one. `hibro-app://workbench/x`
 * and `hibro-assets://any-module/x` both report origin `"null"`, so an origin
 * comparison would read a module's asset address as the workbench itself.
 *
 * Unparseable input answers `false`, which routes it to the guard's blocking
 * branch. That is the safe direction: an address the platform cannot even parse
 * is not one it should let replace the workbench.
 */
export function isSameDocumentAddress(
  targetUrl: string,
  baseUrl: string,
): boolean {
  let target: URL;
  let base: URL;
  try {
    target = new URL(targetUrl);
    base = new URL(baseUrl);
  } catch {
    return false;
  }
  return (
    target.protocol === base.protocol &&
    target.host === base.host &&
    target.pathname === base.pathname
  );
}
